<template>
  <section class="sale-detail-summary">
    <div class="sale-detail-summary-main">
      <article class="sale-detail-card sale-detail-card--accent-primary">
        <header class="sale-detail-card-header sale-detail-card-header--inline">
          <h4>
            <i class="fa fa-building sale-section-icon sale-section-icon--general" aria-hidden="true"></i>
            {{ text.detail.sharedDetailTab.cards.general }}
          </h4>

          <button
            type="button"
            class="sale-section-edit-btn"
            :title="text.detail.sharedDetailTab.editButtons.general"
            @click="$emit('edit-section', 'general')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="sale-detail-info-grid sale-detail-info-grid--general">
          <div
            v-for="item in summaryGeneral"
            :key="item.label"
            class="sale-detail-info-item"
          >
            <span>{{ item.label }}</span>
            <button
              v-if="item.isAction"
              type="button"
              class="sale-detail-info-action"
              @click="$emit('edit-section', item.sectionKey)"
            >
              {{ item.value }}
            </button>
            <span v-else class="sale-detail-info-value">{{ item.value }}</span>
          </div>
        </div>
      </article>

      <article class="sale-detail-card sale-detail-card--accent-scope">
        <header class="sale-detail-card-header sale-detail-card-header--inline">
          <h4>
            <i class="fa fa-bullseye sale-section-icon sale-section-icon--scope" aria-hidden="true"></i>
            {{ text.detail.sharedDetailTab.cards.scope }}
          </h4>

          <button
            type="button"
            class="sale-section-edit-btn"
            :title="text.detail.sharedDetailTab.editButtons.scope"
            @click="$emit('edit-section', 'details')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="sale-detail-info-stack">
          <div class="sale-detail-info-item">
            <span class="sale-detail-info-value">{{ scopeValue }}</span>
          </div>
        </div>
      </article>
    </div>

    <aside class="sale-detail-summary-side">
      <article class="sale-detail-card sale-detail-card--accent-success">
        <header class="sale-detail-card-header sale-detail-card-header--inline">
          <h4>
            <i class="fa fa-money sale-section-icon sale-section-icon--amounts" aria-hidden="true"></i>
            {{ text.detail.sharedDetailTab.cards.amounts }}
          </h4>

          <button
            type="button"
            class="sale-section-edit-btn"
            :title="text.detail.sharedDetailTab.editButtons.amounts"
            @click="$emit('edit-section', 'general')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="sale-detail-info-grid sale-detail-info-grid--amounts">
          <div
            v-for="item in summaryAmounts"
            :key="item.label"
            class="sale-detail-info-item"
          >
            <span>{{ item.label }}</span>
            <span class="sale-detail-info-value">{{ item.value }}</span>
          </div>
        </div>
      </article>

      <article class="sale-detail-card sale-detail-card--accent-address">
        <header class="sale-detail-card-header sale-detail-card-header--inline">
          <h4>
            <i class="fa fa-file-text-o sale-section-icon sale-section-icon--quote" aria-hidden="true"></i>
            {{ text.detail.sharedDetailTab.cards.quotes }}
          </h4>

          <button
            type="button"
            class="sale-section-edit-btn"
            :title="text.detail.sharedDetailTab.editButtons.quotes"
            @click="$emit('edit-section', 'quote')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="sale-detail-info-grid sale-detail-info-grid--quote">
          <div class="sale-detail-info-item">
            <span>{{ text.detail.sharedDetailTab.fields.quotePrincipal }}</span>
            <button
              v-if="quotePrincipalValue.isAction"
              type="button"
              class="sale-detail-info-action"
              @click="$emit('edit-section', quotePrincipalValue.sectionKey)"
            >
              {{ quotePrincipalValue.value }}
            </button>
            <span v-else class="sale-detail-info-value">{{ quotePrincipalValue.value }}</span>
          </div>

          <div class="sale-detail-info-item sale-detail-info-item--full">
            <span>{{ text.detail.sharedDetailTab.fields.quoteExtras }}</span>
            <span class="sale-detail-info-value">{{ quoteExtrasValue }}</span>
          </div>

          <div class="sale-detail-info-item sale-detail-info-item--full">
            <span>{{ text.detail.sharedDetailTab.fields.quoteReference }}</span>
            <button
              v-if="quoteDocumentValue.isAction"
              type="button"
              class="sale-detail-info-action"
              @click="$emit('edit-section', quoteDocumentValue.sectionKey)"
            >
              {{ quoteDocumentValue.value }}
            </button>
            <span v-else class="sale-detail-info-value">{{ quoteDocumentValue.value }}</span>
          </div>
        </div>
      </article>

      <article class="sale-detail-card sale-detail-card--accent-warning">
        <header class="sale-detail-card-header sale-detail-card-header--inline">
          <h4>
            <i class="fa fa-commenting-o sale-section-icon sale-section-icon--comments" aria-hidden="true"></i>
            {{ text.detail.sharedDetailTab.cards.comments }}
          </h4>

          <button
            type="button"
            class="sale-section-edit-btn"
            :title="text.detail.sharedDetailTab.editButtons.comments"
            @click="$emit('edit-section', 'details')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="sale-detail-comments">{{ commentsValue }}</div>
      </article>
    </aside>
  </section>
</template>

<script>
import { formatDateLong } from '@/utils/datePresentation';
import { formatCurrency } from '@/utils/numberFormat';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('sales');

const normalizeText = (value) => String(value || '').trim();

const valueOr = (value, fallback) => {
  const normalized = normalizeText(value);
  return normalized !== '' && normalized !== '-' ? normalized : fallback;
};

const optionalAssignField = (label, value, assignLabel, sectionKey = 'detail') => {
  const normalized = normalizeText(value);

  return {
    label,
    value: normalized !== '' ? normalized : assignLabel,
    isAction: normalized === '',
    sectionKey,
  };
};

const formatTagList = (tags, fallbackLabel) => {
  const values = (Array.isArray(tags) ? tags : [])
    .map((item) => normalizeText(item))
    .filter(Boolean);

  return values.length > 0 ? values.join(' · ') : fallbackLabel;
};

export default {
  name: 'SaleSharedDetailTab',
  props: {
    sale: {
      type: Object,
      default: null,
    },
  },
  emits: ['edit-section'],
  data() {
    return {
      text,
    };
  },
  computed: {
    isAdvertisingModuleActive() {
      const can = typeof this.$can === 'function' ? this.$can : null;
      if (!can) {
        return true;
      }

      return can('sales.advertising');
    },
    summaryGeneral() {
      return [
        { label: this.text.detail.sharedDetailTab.fields.code, value: valueOr(this.sale?.code, this.text.detail.sharedDetailTab.values.pending) },
        { label: this.text.detail.sharedDetailTab.fields.name, value: valueOr(this.sale?.name, this.text.detail.sharedDetailTab.values.pending) },
        { label: this.text.detail.sharedDetailTab.fields.status, value: valueOr(this.sale?.status_name, this.text.detail.sharedDetailTab.values.pending) },
        { label: this.text.detail.sharedDetailTab.fields.customer, value: valueOr(this.sale?.company_name, this.text.detail.sharedDetailTab.values.pending) },
        { label: this.text.detail.sharedDetailTab.fields.serviceType, value: valueOr(this.sale?.service_type_name, this.text.detail.sharedDetailTab.values.pending) },
        { label: this.text.detail.sharedDetailTab.fields.service, value: valueOr(this.sale?.product_name, this.text.detail.sharedDetailTab.values.pending) },
        { label: this.text.detail.sharedDetailTab.fields.saleDate, value: valueOr(formatDateLong(this.sale?.sale_date), this.text.detail.sharedDetailTab.values.pending) },
        { label: this.text.detail.sharedDetailTab.fields.deliveryDate, value: valueOr(formatDateLong(this.sale?.delivery_date), this.text.detail.sharedDetailTab.values.pending) },
        optionalAssignField(this.text.detail.sharedDetailTab.fields.seller, this.sale?.seller_name, this.text.detail.sharedDetailTab.values.assign),
        optionalAssignField(this.text.detail.sharedDetailTab.fields.manager, this.sale?.manager_name, this.text.detail.sharedDetailTab.values.assign),
        { label: this.text.detail.sharedDetailTab.fields.tags, value: formatTagList(this.sale?.tags, this.text.detail.sharedDetailTab.values.noTags) },
        optionalAssignField(this.text.detail.sharedDetailTab.fields.area, this.sale?.area_name, this.text.detail.sharedDetailTab.values.assign),
      ];
    },
    summaryAmounts() {
      const rows = [
        { label: this.text.detail.sharedDetailTab.fields.subtotal, value: formatCurrency(this.sale?.subtotal || this.sale?.sale_price || 0) },
        { label: this.text.detail.sharedDetailTab.fields.budgetGeneral, value: formatCurrency(this.sale?.budget_general || 0) },
      ];

      if (this.isAdvertisingModuleActive) {
        rows.push(
          { label: this.text.detail.sharedDetailTab.fields.mediaAmount, value: formatCurrency(this.sale?.media_amount || this.sale?.advertising_amount || 0) },
          { label: this.text.detail.sharedDetailTab.fields.agencyAmount, value: formatCurrency(this.sale?.agency_amount || this.sale?.sale_import || 0) },
        );
      }

      return rows;
    },
    scopeValue() {
      return valueOr(this.sale?.scope, this.text.detail.sharedDetailTab.values.noScope);
    },
    commentsValue() {
      return valueOr(this.sale?.comments, this.text.detail.sharedDetailTab.values.noComments);
    },
    quotePrincipalValue() {
      const principal = this.sale?.quote_principal || {};
      const value = normalizeText(principal?.label);

      return {
        value: value !== '' ? value : this.text.detail.sharedDetailTab.values.addQuote,
        isAction: value === '',
        sectionKey: 'quote',
      };
    },
    quoteExtrasValue() {
      const rows = Array.isArray(this.sale?.quote_extras) ? this.sale.quote_extras : [];
      const labels = rows
        .map((item) => String(item?.label || '').trim())
        .filter((item) => item !== '');

      return labels.length > 0 ? labels.join(' · ') : this.text.detail.sharedDetailTab.values.noExtraQuotes;
    },
    quoteDocumentValue() {
      const raw = String(this.sale?.quote_document || '').trim();
      const isFile = Number(this.sale?.quote_is_file || 0) === 1;

      if (raw === '') {
        return {
          value: this.text.detail.sharedDetailTab.values.addReference,
          isAction: true,
          sectionKey: 'quote',
        };
      }

      if (isFile) {
        return {
          value: `${this.text.detail.sharedDetailTab.values.quoteFilePrefix} ${raw}`,
          isAction: false,
          sectionKey: '',
        };
      }

      return {
        value: raw,
        isAction: false,
        sectionKey: '',
      };
    },
  },
};
</script>
